/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BackupEnum } from '../../../shared/enums/api';
import { BackupRestoreApiService } from '../../../shared/http';
import { BaseComponent } from '../../../../../core/components';
import { Component, OnInit } from '@angular/core';
import { DownloadService, NotifyService, RbacService } from '../../../../../core/services';
import { ErrorResponseModel } from '../../../../../core/models';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-backup-restore',
    templateUrl: './backupRestore.component.html',
    styleUrls: ['./backupRestore.component.scss']
})

export class BackupRestoreComponent extends BaseComponent implements OnInit {
    /**
     * To store the backups.
     */
    public backups: { uploads: any; auto: any; };

    /**
     * Table row data
     */
    public uploadBackUpTable: any[] = [];

    /**
     * To store the auto back up table data.
     */
    public autoBackUpTable: any[] = [];

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    constructor(
        private backupApiService: BackupRestoreApiService,
        private downloadFile: DownloadService,
        private notifyService: NotifyService,
        private rbacService: RbacService,
    ) {
        super();
    }

    /**
     * Ng On Init
     */
    public ngOnInit(): void {
        this.setUpPageData();
        this.registerButtons();
    }

    /**
     * Send an HTTP request to download a file.
     */
    public backup(): Observable<any> {
        return new Observable<any>(observer => {
            this.downloadFile.downloadFile({ url: BackupEnum.GET_BACKUP, appID: false })
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.notifyService.success(
                            'backup_restore_download_complete', { messageTranslate: true }
                        );
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.notifyService.error('Error downloading backup ' + error);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Set up page data.
     * @private
     */
    private setUpPageData(): void {
        this.backupApiService.getBackupList()
            .pipe(takeUntil(this.destroyed$))
            .subscribe(
                data => {
                    this.backups = data;
                    this.populateTables();
                }
            );
    }

    /**
     * Populate auto and uploaded backups tables.
     * @private
     */
    private populateTables(): void {
        this.uploadBackUpTable = this.populateBackupTableRows(this.backups.uploads);
        this.autoBackUpTable = this.populateBackupTableRows(this.backups.auto);
    }

    /**
     * Add results to the back-up table.
     * @param data
     * @private
     */
    private populateBackupTableRows(data: any[]): any[] {
        const tableRows: any[] = [];
        if (data?.length) {
            for (const backup of data) {
                tableRows.push({
                    description: this.getDescription(backup.file).replace(/\s+/g, ' ').trim(),
                    change: this.getChangeName(backup.file),
                    viewDateTime: backup.dateTime.replace('+0000', ''),
                    dateTime: backup.dateTime,
                    file: backup.file,
                    nodes: backup.nodes,
                    sections: backup.sections
                });
            }
        }

        return tableRows;
    }

    /**
     * Get the description.
     * @param file
     * @private
     */
    private getDescription(file: string) {
        const matches = file.match(/api-v1-(.*)/);
        if (!matches || !matches.length) {
            return file.replace(/\.json$/, '');
        }

        const description = this.removeIdFromString(matches[1])
            .replace(/\.json$/, '')
            .replace(/-/g, ' ')
            .trim();

        return this.convertDescriptionToTitleCase(description);
    }

    /**
     * Remove IDs from the description
     * @param match
     * @private
     */
    private removeIdFromString(match: string): string {
        let description: string;
        if (match.match(/^[a-fA-F0-9]{3}-/)) {
            const regexPattern = /[a-fA-F0-9]{3}-[a-fA-F0-9]{3}-[a-fA-F0-9]{3}-[a-fA-F0-9]{3}/;
            description = match.replace(regexPattern, '');
        } else {
            const regexPattern = /-[a-fA-F0-9]{3}-[a-fA-F0-9]{3}-[a-fA-F0-9]{3}-[a-fA-F0-9]{3}/;
            description = match.replace(regexPattern, '');
        }

        return description;
    }

    /**
     * Convert description to title case
     * @param description
     * @private
     */
    private convertDescriptionToTitleCase(description: string): string {
        const words: string[] = description.split(' ');
        for (let i = 0; i < words.length; i++) {
            if (this.isWordInDictionary(words[i])) {
                words[i] = words[i].toUpperCase();
            } else {
                words[i] = words[i][0].toUpperCase() + words[i].substring(1);
            }
        }

        return words.join(' ');
    }

    /**
     * Check if the word is in the dictionary.
     * @param word
     * @private
     */
    private isWordInDictionary(word: string): boolean {
        return ['rbac', 'ntp'].includes(word);
    }

    /**
     * Get the change name.
     * @param file
     * @private
     */
    private getChangeName(file: string): string {
        const matches = file.match(/(?<=_)([A-Z]{3,6})(?=-)/gi);
        if (matches?.length) {
            return this.getChangeFriendlyName(matches[0]);
        } else {
            return '';
        }
    }

    /**
     * Get the user-friendly label for changes.
     * @param name
     * @private
     */
    private getChangeFriendlyName(name: string): string {
        const changes = {
            POST: 'Added',
            PUT: 'Edited',
            DELETE: 'Deleted'
        };

        return changes[name];
    }

    /**
     * Register the page's buttons for RBAC.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'backup-restore-upload-backup': [{ path: '/backup/upload', methods: ['POST'] }],
            'backup-restore-download-backup': [{ path: '/backup', methods: ['GET'] }],
            'backup-restore-restore': [{ path: '/backup/restore', methods: ['POST'] }]
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }
}
